import type { ElementText, SlideElement, TextParagraph, TextRun } from '../types';

const paragraphText = (paragraph: TextParagraph) => paragraph.runs.map((run) => run.text).join('');

/** De tekst van een element als platte tekst: één regel per alinea. */
export function textOf(element: SlideElement): string {
  return (element.text?.paragraphs || []).map(paragraphText).join('\n');
}

/**
 * Past de tekst van een element aan zonder opmaak te verliezen. Een ongewijzigde alinea blijft precies zoals ze
 * was (inclusief gemengde opmaak). Een gewijzigde of nieuwe alinea neemt de alinea-opmaak en de opmaak van de
 * eerste run over van de alinea op dezelfde plaats (of de laatste). Gemengde opmaak binnen een gewijzigde
 * alinea wordt dus samengevoegd tot één stijl.
 */
export function applyText(element: SlideElement, value: string): SlideElement {
  const old = element.text?.paragraphs || [];
  const lines = value.split('\n');
  const fallback: TextParagraph = old[old.length - 1] || { runs: [{ text: '' }], align: 'left' };
  const paragraphs = lines.map((line, index): TextParagraph => {
    const source = old[index];
    if (source && paragraphText(source) === line) return source;
    const template = source || fallback;
    const firstRun: TextRun = template.runs[0] || { text: '' };
    return { ...template, runs: [{ ...firstRun, text: line }] };
  });
  const text: ElementText = { anchor: 'top', padding: 7, ...(element.text || {}), paragraphs };
  return { ...element, text };
}

/** Past een opmaakwijziging toe op alle runs van een element. */
export function styleRuns(element: SlideElement, change: Partial<TextRun>): SlideElement {
  if (!element.text) return element;
  return {
    ...element,
    text: {
      ...element.text,
      paragraphs: element.text.paragraphs.map((paragraph) => ({
        ...paragraph, runs: paragraph.runs.map((run) => ({ ...run, ...change })),
      })),
    },
  };
}

export function styleParagraphs(element: SlideElement, change: Partial<TextParagraph>): SlideElement {
  if (!element.text) return element;
  return { ...element, text: { ...element.text, paragraphs: element.text.paragraphs.map((p) => ({ ...p, ...change })) } };
}

/** Eigenschap van de eerste run, voor weergave in het opmaakpaneel. */
export function firstRun(element: SlideElement): TextRun | undefined {
  return element.text?.paragraphs.find((p) => p.runs.length)?.runs[0];
}
